import { RadioGroup } from '@headlessui/react';
import {
  ChangeEvent,
  ForwardedRef,
  InputHTMLAttributes,
  forwardRef,
} from 'react';
import { Controller, FieldValues, RegisterOptions } from 'react-hook-form';
import { ISelectedOptionCustomizationProps } from '../address/billingFormFields/SelectedOptionCustomization';

export interface RadioOptions {
  id: string;
  name: string;
  value: boolean;
}

interface IRadioProps extends InputHTMLAttributes<HTMLInputElement> {
  options: RadioOptions[];
  radioColor?: string;
  radioSize?: string;
  placeHolder?: {
    header: string;
    same: string;
    different: string;
  };
  className?: string;
  error?: string;
  background?: {
    color: string;
    isInherit: boolean;
  };
  selectedOptionCustomization?: ISelectedOptionCustomizationProps;
  styles?: Record<string, string>;
  currentDeviceType?: string;
}

interface IRadioControlProps extends IRadioProps {
  name: string;
  background?: {
    color: string;
    isInherit: boolean;
  };
  rules?: Omit<
    RegisterOptions<FieldValues, string>,
    'disabled' | 'setValueAs' | 'valueAsNumber' | 'valueAsDate'
  >;
  selectedOptionCustomization?: ISelectedOptionCustomizationProps;
  currentDeviceType?: string;
}

export const RadioControl = ({
  name,
  background,
  rules,
  radioColor,
  radioSize,
  selectedOptionCustomization,
  currentDeviceType,
  ...rest
}: IRadioControlProps) => {
  return (
    <Controller
      name={name}
      render={({
        field: { value, onChange, ...restField },
        fieldState: { error },
      }) => {
        return (
          <Radio
            {...rest}
            radioColor={radioColor}
            radioSize={radioSize}
            checked={value}
            background={background}
            onChange={onChange}
            selectedOptionCustomization={selectedOptionCustomization}
            currentDeviceType={currentDeviceType}
            {...restField}
            error={error?.message}
          />
        );
      }}
      rules={rules}
    />
  );
};

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

export const Radio = forwardRef(function RadioInternal(
  {
    onChange,
    background,
    options,
    placeHolder,
    checked,
    radioColor = '',
    radioSize,
    selectedOptionCustomization,
    ...rest
  }: IRadioProps,
  ref: ForwardedRef<HTMLInputElement>,
) {
  const handleChange = (value: boolean) => {
    if (onChange) {
      onChange(value as unknown as ChangeEvent<HTMLInputElement>);
    }
  };
  radioSize += 'px';
  return (
    <RadioGroup value={checked} onChange={handleChange}>
      <div className="text-sm text-gray-500 mb-2">
        <div dangerouslySetInnerHTML={{ __html: placeHolder?.header || '' }} />
      </div>
      <div
        style={{
          backgroundColor: background?.isInherit
            ? 'inherit'
            : background?.color,
        }}
        className={`-space-y-px rounded-md`}>
        {options?.map((option, optionIdx) => (
          <RadioGroup.Option
            {...rest}
            ref={ref}
            key={option.id}
            value={option.value}>
            {({ checked }) => (
              <div
                className={`${optionIdx === 0 ? 'rounded-tl-md rounded-tr-md' : ''}  ${
                  optionIdx === options.length - 1
                    ? 'rounded-bl-md rounded-br-md'
                    : ''
                }
        ${checked ? 'z-5' : 'border-gray-200'}
        relative flex cursor-pointer border p-4 focus:outline-none`}
                style={
                  checked
                    ? {
                        backgroundColor: selectedOptionCustomization?.background
                          ?.isInherit
                          ? 'inherit'
                          : selectedOptionCustomization?.background?.color,
                        borderColor: selectedOptionCustomization?.borderColor,
                        position: 'relative',
                      }
                    : {}
                }>
                <>
                  <span
                    style={{
                      backgroundColor: checked ? radioColor : 'white',
                      width: radioSize,
                      height: radioSize,
                    }}
                    className={classNames(
                      checked
                        ? 'border-transparent'
                        : 'bg-white border-gray-300',
                      'mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded-full border flex items-center justify-center',
                    )}
                    aria-hidden="true">
                    <span className="rounded-full bg-white w-1.5 h-1.5" />
                  </span>
                  <span className="ml-3 flex flex-col">
                    <RadioGroup.Label
                      as="span"
                      className={classNames('text-gray-900', 'block text-sm ')}>
                      <span dangerouslySetInnerHTML={{ __html: option.name }} />
                    </RadioGroup.Label>
                  </span>
                </>
              </div>
            )}
          </RadioGroup.Option>
        ))}
      </div>
    </RadioGroup>
  );
});
